import React, { useMemo, useState } from 'react';
import {
	User,
	useSimpleWorkerInformationQuery,
	useWorkerWorkplacesQuery,
	WorkplaceMember,
} from '../../../../../graphql/graphql';
import Loading from '../../../../../Components/Loading';
import UserAvatarImage from '../../../../../Components/UserAvatar/UserAvatarImage';
import styles from './WorkplaceWorkerDetail.module.scss';
import TherapistDetail from '../../Workers/TherapistDetail/TherapistDetail';
import WorkerDetail from '../../Workers/WorkerDetail/WorkerDetail';
import { useAppContext } from '../../../../../Services/AppContext';
import Text from '../../../../../Components/Base/Text/Text';
import WorkplaceWorkerWorkplacesList from './WorkplaceWorkerWorkplacesList';
import useTranslation from '../../../../../Hooks/useTranslation';
import WorkplaceWorkerSUKLLogin from '../WorkplaceWorkerSUKLLogin/WorkplaceWorkerSUKLLogin';
import useScope from '../../../../Scope/Hooks/useScope';
import WorkplaceWorkerHealthCareWorkerCode from '../WorkplaceWorkerHealthCareWorkerCode/WorkplaceWorkerHealthCareWorkerCode';
import ManagerEditMemberProfileModal from '../../ManagerWrapper/Workers/ManagerEditMemberProfile/ManagerEditMemberProfileModal';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import WorkplaceMemberDetailItem from './WorkplaceMemberDetailItem';
import { useAclContext } from '../../../../Acl/Components/AclContextProvider';
import WorkplaceWorkerInsuranceSettings from '../WorkplaceWorkerInsuranceSettings/WorkplaceWorkerInsuranceSettings';

interface Props {
	userId: string;
	showAllWorkplaces: boolean;
	setDoListRefresh: (doListRefresh: boolean) => void;
	closeAndReload: () => void;
}

const WorkplaceWorkerDetail: React.FC<Props> = ({
	userId,
	showAllWorkplaces,
	setDoListRefresh,
	closeAndReload,
}) => {
	const { t } = useTranslation();
	const { isLatam, workplaceId, isMou } = useAppContext();
	const { isSlovakia } = useScope();
	const {
		data: userData,
		isLoading: isLoadingUserData,
		refetch: reloadUserData,
	} = useSimpleWorkerInformationQuery({ userId });
	const {
		data: workersWorkplaces,
		isLoading: isLoadingWorkersWorkplaces,
		refetch: reloadWorkersWorkplaces,
	} = useWorkerWorkplacesQuery({
		userId,
		rootWorkplaceId: workplaceId,
	});
	const [isTherapist, setIsTherapist] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const { isAllowed } = useAclContext();

	const displayedWorkplaces = useMemo(() => {
		const temp: WorkplaceMember[] = [];
		workersWorkplaces?.workplaceMembers?.data?.forEach(item => {
			if (showAllWorkplaces) {
				temp.push(item as WorkplaceMember);
			} else if (item?.workplace?.id === workplaceId) {
				temp.push(item as WorkplaceMember);
			}
			setIsTherapist(
				item?.workplaceMemberRoles?.some(
					workplaceMemberRole => workplaceMemberRole?.role?.key === 'therapist',
				) ?? false,
			);
		});
		return temp;
	}, [workersWorkplaces]);

	const handleOnWorkerWorkplacesUpdate = () => {
		setDoListRefresh(true);
		reloadWorkersWorkplaces();
	};

	const handleOnWorkerProfileUpdate = async () => {
		setIsEditOpen(false);
		setDoListRefresh(true);
		await reloadUserData();
	};

	if (isLoadingUserData || isLoadingWorkersWorkplaces) {
		return <Loading />;
	}

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.left}>
					<UserAvatarImage
						userId={userId}
						profileImageUrl={userData?.user?.profileImageUrl}
					/>
					{userData?.user?.profile?.fullName && (
						<div className={styles.name}>{userData.user.profile.fullName}</div>
					)}
					{userData?.user?.email && (
						<Text tx={userData.user.email} truncate={30} />
					)}
					{userData?.user?.formattedPhoneNumber && (
						<div>{userData.user.formattedPhoneNumber}</div>
					)}
					{isAllowed('manager_doctor_detail') && (
						<LinkButton
							title='component.workplaceWorkerDetail.editProfile'
							theme={Theme.BLUE}
							className='mt-3'
							onClick={() => setIsEditOpen(true)}
						/>
					)}
				</div>
				<div className={styles.right}>
					{isTherapist && (
						<WorkplaceMemberDetailItem title='component.doctorDetailComponent.therapistSettings'>
							<TherapistDetail userId={userId} />
						</WorkplaceMemberDetailItem>
					)}
					{isLatam && (
						<WorkplaceMemberDetailItem title='base.shared.profile'>
							<WorkerDetail userId={userId} />
						</WorkplaceMemberDetailItem>
					)}
					{isMou && (
						<WorkplaceMemberDetailItem title='component.userSuklLoginCredentials.title'>
							<WorkplaceWorkerSUKLLogin userId={userId} />
						</WorkplaceMemberDetailItem>
					)}
					{isSlovakia && (
						<>
							<WorkplaceMemberDetailItem>
								<WorkplaceWorkerHealthCareWorkerCode userId={userId} />
							</WorkplaceMemberDetailItem>
							<WorkplaceMemberDetailItem>
								<WorkplaceWorkerInsuranceSettings userId={userId} />
							</WorkplaceMemberDetailItem>
						</>
					)}
					<div className={styles.part}>
						<WorkplaceWorkerWorkplacesList
							data={displayedWorkplaces}
							showAllWorkplaces={showAllWorkplaces}
							userId={userId}
							onUpdate={handleOnWorkerWorkplacesUpdate}
							onDeleteFromAllWorkplaces={closeAndReload}
						/>
					</div>
				</div>
			</div>
			{isEditOpen && userData?.user && (
				<ManagerEditMemberProfileModal
					isOpen
					workplaceMemberId={userId}
					user={userData.user as User}
					onClose={() => setIsEditOpen(false)}
					onSuccess={handleOnWorkerProfileUpdate}
				/>
			)}
		</>
	);
};

export default WorkplaceWorkerDetail;
